import { ContentSurfaceWidget } from '../../widgets/ContentSurfaceWidget';
import { Gallery, GalleryItem } from '../../widgets/DocsGalleryWidget';
import styled from '@emotion/styled';
import * as React from 'react';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { NotFoundPage } from '../NotFoundPage';
import { PHONE_MEDIA } from '../../vars';
import { DocsCodeBlockWidget } from '../../widgets/DocsCodeBlockWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { ComparisonWidget } from '../../widgets/ComparisonWidget';
import { CrumbWidget } from '../../widgets/CrumbWidget';
import { Info, Warning } from '../../widgets/DocsCalloutWidget';
import { FAQ } from '../../widgets/FAQWidget';
import { CenterWidget } from '../../widgets/layout/CenterWidget';
import { LayoutClassName } from '../../widgets/layout/LayoutClassName';
import { TOCPageWidget } from '../../widgets/layout/TOCPageWidget';
import { TagBadgeWidget } from '../../widgets/TagBadgeWidget';
import { TableOfContentsItem } from '../../widgets/TableOfContentsWidget';
import { CardTagWidget } from '../../widgets/CardTagWidget';
import { BLOG_CATEGORY_TONES } from './shared/blogCategories';
import { InlineLink } from '../shared/InlineLink';
import { MdxLink } from '../shared/MdxLink';
import { MdxTable } from '../shared/MdxTable';
import { BLOG_POSTS, normalizePermalink } from './shared/blog';

export const BlogPostPage: React.FC = () => {
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const requestedPermalink = normalizePermalink(location.pathname);

  const found = useMemo(
    () => BLOG_POSTS.find((post) => normalizePermalink(post.frontmatter.permalink) === requestedPermalink),
    [requestedPermalink]
  );
  const tableOfContents: TableOfContentsItem[] = found?.tableOfContents ?? [];
  const mdxComponents = useMemo(
    () => ({
      ComparisonWidget,
      FAQ,
      Info,
      Warning,
      TagBadgeWidget,
      InlineLink,
      Gallery,
      GalleryItem,
      pre: DocsCodeBlockWidget,
      table: MdxTable,
      a: MdxLink
    }),
    []
  );

  useLayoutEffect(() => {
    scrollContainerRef.current = document.querySelector<HTMLElement>(`.${LayoutClassName.SCROLL_CONTAINER}`);
  }, []);

  if (!found) {
    return <NotFoundPage />;
  }

  const PostContent = found.default as React.ComponentType<{ components?: Record<string, React.ComponentType<any>> }>;

  return (
    <div>
      <S.Hero url={found.frontmatter.cover_image}>
        <S.HeroContent>
          <S.HeroMeta>
            <S.Title>
              <CrumbWidget parts={['Blog', found.frontmatter.title]} />
            </S.Title>
            <S.Meta>
              <S.When>{found.date.toISODate() ?? ''}</S.When>
              <S.When>By {found.frontmatter.author}</S.When>
            </S.Meta>
            <S.Categories>
              {found.categories.map((c) => (
                <S.Category key={c} $tone={BLOG_CATEGORY_TONES[c]}>
                  {c}
                </S.Category>
              ))}
            </S.Categories>
          </S.HeroMeta>
        </S.HeroContent>
      </S.Hero>
      <TOCPageWidget
        title={`Post: ${found.frontmatter.title}`}
        contentWidth={864}
        tableOfContents={tableOfContents}
      >
        <S.ArticleContent>
          <ContentWidget variant="article">
            <PostContent components={mdxComponents} />
          </ContentWidget>
        </S.ArticleContent>
      </TOCPageWidget>
    </div>
  );
};

namespace S {
  export const Hero = styled.div<{ url: string }>`
    background: url(${(p) => p.url});
    background-size: cover;
    height: 400px;
    position: relative;
    background-position: center center;

    @media (max-width: ${PHONE_MEDIA}px) {
      height: auto;
    }
  `;

  export const HeroContent = styled.div`
    background: ${(p) => p.theme.layout.fade};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;

    @media (max-width: ${PHONE_MEDIA}px) {
      position: relative;
      padding-bottom: 30px;
      padding-top: 30px;
    }
  `;

  export const When = styled.div`
    font-size: 20px;
  `;

  export const Meta = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    column-gap: 20px;
  `;

  export const Categories = styled.div`
    display: flex;
    align-items: center;
    column-gap: 10px;
  `;

  export const Category = styled(CardTagWidget)`
    border: 1px solid ${(p) => p.theme.selectionPill.tones[p.$tone ?? 'interface'].border};
  `;

  export const HeroMeta = styled(CenterWidget)`
    display: flex;
    flex-direction: column;
    row-gap: 20px;
  `;

  export const Title = styled.h1`
    max-width: 980px;
  `;

  export const ArticleContent = styled(ContentSurfaceWidget)``;
}
